import chunk from 'lodash/chunk';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import isEmail from 'validator/lib/isEmail';

import { isValidChannelUrl } from 'utils/validators';

import {
  CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER
} from '../constants/common';
import {
  AVAILABLE_CHANNELS_DICTIONARY
} from '../constants/entity';

import {
  CreateNewProfilesEntity,
  CreateNewProfilesProfilesState
} from '../types/CreateNewProfiles.type';
import {
  LightweighInfluencerCountry,
  LightweighInfluencerGender
} from '../types/general';

const isArtistNameValid = (name: string | null): boolean => (
  name !== null && name.trim().length > 0 && !/(^https:\/\/)?(www\.)?.+\.com\/.+/.test(name)
);

const isEmailValid = (email: string | null): boolean => (
  email === null || email.length === 0 || isEmail(email)
);

const isValidChannel = (url: string): boolean => (
  Object
    .keys(AVAILABLE_CHANNELS_DICTIONARY)
    .map(channelType => isValidChannelUrl(channelType, url))
    .some(result => result)
);

const areChannelsValid = (urls: Array<string>): boolean => (
  urls.reduce(
    (acc, url) => (
      acc && isValidChannel(url)
    ),
    urls.length > 0
  )
);

const areChannelsWithValidLength = (urls: Array<string>): boolean => (
  urls.reduce(
    (acc, url) => (
      acc && url.length <= 255
    ),
    urls.length > 0
  )
);

type InvalidFields = Exclude<CreateNewProfilesEntity['invalidFields'], undefined>;

export const validateEntity = (
  entity: CreateNewProfilesEntity
): true | InvalidFields => {
  const fields: InvalidFields = [];

  if (!isArtistNameValid(entity.artistName)) {
    fields.push('name');
  } else if (entity.channelUrls.some(url => url.includes('\n'))) {
    fields.push('separator');
  } else if (!areChannelsValid(entity.channelUrls)) {
    fields.push('urls');
  } else if (!areChannelsWithValidLength(entity.channelUrls)) {
    fields.push('urlLength');
  } else if (!isEmailValid(entity.email)) {
    fields.push('email');
  }

  return fields.length === 0
    ? true
    : fields;
};

type EntityValidationResult = [boolean, Array<CreateNewProfilesEntity>];

export const getEntityValidationResult = (
  entities: Array<CreateNewProfilesEntity>
): EntityValidationResult => (
  entities.reduce(
    (acc, entity) => {
      const validationResult = validateEntity(entity);

      if (typeof validationResult === 'boolean' && validationResult) {
        return [acc[0], [...acc[1], entity]];
      }

      return [false, [...acc[1], { ...entity, invalidFields: validationResult }]];
    },
    [true, []] as EntityValidationResult
  )
);

type CreateNewProfilesNonNullableProfileEntity = {
  artistName: string,
  channelUrls: Array<string>,
  locations: Array<LightweighInfluencerCountry>,
  gender?: LightweighInfluencerGender,
  email?: string
}

export const toNonNullableEntities = (
  entities: Array<CreateNewProfilesEntity>
): Array<CreateNewProfilesNonNullableProfileEntity> => (
  entities.reduce(
    (acc, entity) => {
      if (entity.artistName === null) {
        return acc;
      }

      const nonNullableEntity: CreateNewProfilesNonNullableProfileEntity = {
        artistName: entity.artistName,
        channelUrls: entity.channelUrls,
        locations: entity.locations
      };

      if (entity.email !== null) {
        nonNullableEntity.email = entity.email;
      }

      if (entity.gender !== null) {
        nonNullableEntity.gender = entity.gender;
      }

      acc.push(nonNullableEntity);

      return acc;
    },
    [] as Array<CreateNewProfilesNonNullableProfileEntity>
  )
);

export const getEntitiesFromState = (
  state: CreateNewProfilesProfilesState
): Array<CreateNewProfilesEntity> => {
  const { createEntitiesModal } = state;
  let entities: Array<CreateNewProfilesEntity> = [];

  if (createEntitiesModal.type === CREATE_NEW_PROFILES_MODAL_MANUAL_IDENTIFIER) {
    entities = createEntitiesModal.entities;
  } else if (createEntitiesModal.data?.type === 'successful-upload') {
    entities = createEntitiesModal.data.entities;
  }

  return entities;
};

interface ErrorTextsDictionatry {
  name: string,
  urls: string,
  separator: string,
  email: string
}

type ValidatedEntity = {
  invalidFields?: CreateNewProfilesEntity['invalidFields']
}

export const getValidationErrorsForEntities = (
  entities: Array<ValidatedEntity>,
  currentPage: number,
  perPage: number,
  errorTexts: ErrorTextsDictionatry
): Array<string> => (
  chunk(entities, perPage)
    .reduce(
      (acc, page, index) => {
        const addPage = (index + 1) !== currentPage;
        const errorFields = uniq(flatten(page.map(entity => entity.invalidFields ?? [])));
        const errors = errorFields.map(err => {
          if (addPage) {
            return `Page №${index + 1}: ${errorTexts[err]}`;
          }

          return errorTexts[err];
        });

        return addPage
          ? [...acc, ...errors]
          : [...errors, ...acc];
      },
      [] as Array<string>
    )
);

export const getEntityIndex = (
  index: number,
  page: number,
  perPage: number
): number => {
  if (page === 1) {
    return index;
  }

  return ((page - 1) * perPage) + index;
};
